import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle, ListChecks, X } from 'lucide-react';
import { VENDOR_PAGE_GUIDES } from '../../../utils/vendorPageGuides';

/**
 * Consistent page title + optional primary action for vendor screens.
 */
export function VendorPageHeader({
  title,
  subtitle,
  actions = null,
  className = '',
}) {
  return (
    <div
      className={`flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ${className}`}
    >
      <div className="min-w-0">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50 mb-1.5">
          {title}
        </h1>
        {subtitle ? (
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Uniform outer shell for vendor pages rendered inside VendorLayout.
 * Layout already provides page padding — do not add another outer p-* here.
 */
export function VendorPageShell({
  title,
  subtitle,
  actions = null,
  guideKey = null,
  guideTone = 'sky',
  children,
  className = '',
  contentClassName = 'space-y-5',
}) {
  const guide = guideKey ? VENDOR_PAGE_GUIDES[guideKey] : null;

  return (
    <div className={`${contentClassName} ${className}`}>
      <VendorPageHeader title={title} subtitle={subtitle} actions={actions} />
      {guide ? (
        <VendorGuideCallout
          title={guide.title}
          steps={guide.steps}
          tone={guideTone}
          icon={ListChecks}
        />
      ) : null}
      {children}
    </div>
  );
}

/**
 * Soft how-to / tip strip under the page header.
 */
export function VendorGuideCallout({
  title,
  children,
  steps = [],
  tone = 'sky',
  icon: Icon = ListChecks,
  action = null,
  className = '',
}) {
  const tones = {
    sky: {
      wrap: 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/80',
      iconWrap: 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200',
      title: 'text-gray-900 dark:text-gray-100',
      body: 'text-gray-600 dark:text-gray-400',
      step: 'text-gray-500 dark:text-gray-500',
    },
    amber: {
      wrap: 'border-amber-200/80 bg-amber-50/80 dark:border-amber-800/50 dark:bg-amber-950/30',
      iconWrap: 'bg-white border-amber-100 text-amber-700 dark:bg-amber-950/50 dark:border-amber-800 dark:text-amber-300',
      title: 'text-amber-950 dark:text-amber-100',
      body: 'text-amber-900/80 dark:text-amber-200/80',
      step: 'text-amber-700 dark:text-amber-400',
    },
    emerald: {
      wrap: 'border-emerald-200/80 bg-emerald-50/80 dark:border-emerald-800/50 dark:bg-emerald-950/30',
      iconWrap: 'bg-white border-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:border-emerald-800 dark:text-emerald-300',
      title: 'text-emerald-950 dark:text-emerald-100',
      body: 'text-emerald-900/80 dark:text-emerald-200/80',
      step: 'text-emerald-700 dark:text-emerald-400',
    },
    slate: {
      wrap: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
      iconWrap: 'bg-gray-50 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200',
      title: 'text-gray-900 dark:text-gray-100',
      body: 'text-gray-600 dark:text-gray-400',
      step: 'text-gray-500 dark:text-gray-500',
    },
  };
  const t = tones[tone] || tones.sky;

  return (
    <div className={`rounded-xl border px-4 py-3.5 sm:px-5 sm:py-4 shadow-sm ${t.wrap} ${className}`}>
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${t.iconWrap}`}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          {title ? <h2 className={`text-sm font-semibold tracking-tight ${t.title}`}>{title}</h2> : null}
          {children ? <div className={`mt-1 text-sm leading-relaxed ${t.body}`}>{children}</div> : null}
          {steps.length > 0 ? (
            <ol className={`mt-2.5 space-y-1 text-sm leading-snug ${t.title}`}>
              {steps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className={`font-semibold shrink-0 tabular-nums ${t.step}`}>{i + 1}.</span>
                  <span className={t.body}>{step}</span>
                </li>
              ))}
            </ol>
          ) : null}
          {action ? <div className="mt-3">{action}</div> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Friendly empty state with optional CTA.
 */
export function VendorEmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionTo,
  onAction,
  tips = [],
  className = '',
}) {
  return (
    <div
      className={`bg-white border border-dashed border-gray-300 rounded-xl p-8 sm:p-10 text-center ${className}`}
    >
      {Icon ? <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" aria-hidden /> : null}
      <p className="text-gray-900 font-semibold mb-1">{title}</p>
      {description ? <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">{description}</p> : null}
      {tips.length > 0 ? (
        <ul className="text-left max-w-sm mx-auto mb-5 space-y-1.5 text-sm text-gray-600">
          {tips.map((tip, i) => (
            <li key={i} className="flex gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {actionLabel && (actionTo || onAction) ? (
        actionTo ? (
          <Link
            to={actionTo}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          >
            {actionLabel}
            <ArrowRight className="w-4 h-4" />
          </button>
        )
      ) : null}
    </div>
  );
}

/**
 * Start-to-finish setup checklist for the vendor dashboard.
 */
export function VendorGettingStarted({
  steps = [],
  onDismiss,
  className = '',
}) {
  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length || 1;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === steps.length && steps.length > 0;

  if (!steps.length) return null;

  return (
    <div
      className={`rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden ${className}`}
    >
      <div className="flex items-start justify-between gap-3 px-5 pt-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1">
            Getting started
          </p>
          <h2 className="text-lg font-bold text-gray-900 tracking-tight">
            Set up your vendor account
          </h2>
          <p className="mt-1 text-sm text-gray-600 leading-relaxed">
            Follow these steps so clients can find you, book you, and pay you.
          </p>
        </div>
        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Dismiss getting started"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      <div className="px-5 mt-4">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
          <span>
            {doneCount} of {steps.length} complete
          </span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-gray-900 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <ol className="mt-4 divide-y divide-gray-100">
        {steps.map((step) => {
          const StepIcon = step.done ? CheckCircle2 : Circle;
          return (
            <li key={step.id} className="px-5 py-3.5 flex items-start gap-3">
              <StepIcon
                className={`w-5 h-5 shrink-0 mt-0.5 ${
                  step.done ? 'text-emerald-600' : 'text-gray-300'
                }`}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p
                      className={`text-sm font-semibold ${
                        step.done ? 'text-gray-500 line-through' : 'text-gray-900'
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{step.description}</p>
                  </div>
                  {!step.done && step.href ? (
                    <Link
                      to={step.href}
                      className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-gray-900 hover:text-gray-700"
                    >
                      {step.cta || 'Continue'}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {allDone ? (
        <div className="px-5 py-3 bg-emerald-50 border-t border-emerald-100 text-sm text-emerald-800">
          You&apos;re set up. Keep an eye on Bookings and grow with Portfolio, Invoices, and Contracts.
        </div>
      ) : null}
    </div>
  );
}
